import { useEffect, useRef } from 'react';
import moment from 'moment';
import './css/CustomerSignature.css';

interface SignaturePoint {
  x: number;
  y: number;
  type: 'tap' | 'move';
  pressure?: number;
}

interface SignatureData {
  version: number;
  canvasWidth: number;
  canvasHeight: number;
  points: SignaturePoint[];
}

const CustomerSignature = ({
  customerSignature,
  signedAt
}: {
  customerSignature?: string;
  signedAt?: string | { $date: string };
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!customerSignature || !canvasRef.current) return;

    let signatureData: SignatureData;
    try {
      signatureData = JSON.parse(customerSignature);
    } catch {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx || !signatureData.points?.length) return;

    const width = signatureData.canvasWidth || 300;
    const height = signatureData.canvasHeight || 150;
    const maxWidth = 120;
    const scale = maxWidth / width;

    canvas.width = width * scale;
    canvas.height = height * scale;

    ctx.scale(scale, scale);
    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let isDrawing = false;

    signatureData.points.forEach((point) => {
      const x = point.x * width;
      const y = point.y * height;

      if (point.type === 'tap') {
        if (isDrawing) {
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.moveTo(x, y);
        isDrawing = true;
      } else if (point.type === 'move' && isDrawing) {
        ctx.lineTo(x, y);
      }
    });

    if (isDrawing) {
      ctx.stroke();
    }
  }, [customerSignature]);

  if (!customerSignature) {
    return null;
  }

  let formattedSignedAt = '';
  if (signedAt) {
    if (typeof signedAt === 'string') {
      formattedSignedAt = moment(signedAt).format('D MMM YYYY, h:mm A');
    } else if (signedAt.$date) {
      formattedSignedAt = moment(signedAt.$date).format('D MMM YYYY, h:mm A');
    }
  }

  return (
    <div className="customer-signature-container">
      <div className="border-line-1"></div>
      <div className="customer-signature-label">Customer Signature</div>
      <div className="customer-signature-canvas-wrapper">
        <canvas ref={canvasRef} className="customer-signature-canvas" />
      </div>
      {formattedSignedAt && (
        <div className="customer-signature-date">Signed: {formattedSignedAt}</div>
      )}
    </div>
  );
};

export default CustomerSignature;
